import React, {
  useEffect, useState, useCallback,
} from 'react';
import { ChromePicker, ColorState } from 'react-color';
import {
  createStyles,
  makeStyles,
  Button,
  Grid,
  Paper,
  Theme,
} from '@material-ui/core';

import clsx from 'clsx';
import { useOuterClick } from '../../hooks';
import { SortUpIcon, SortDownIcon } from '../icon/solid';

const useStyles = makeStyles(({ colors, palette }: Theme) => createStyles({
  button: {
    width: '85px',
    height: '40px',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px',
    cursor: 'pointer',
    '&:disabled': {
      cursor: 'not-allowed',
    },
    '&:hover $colorBox': {
      border: '1px solid rgba(0, 0, 0, 0.23)',
    },
  },
  colorBox: {
    width: '16px',
    height: '16px',
  },
  buttonIcon: {
    width: '12px',
    height: '12px',
  },
  wrapper: {
    width: '230px',
    position: 'absolute',
    zIndex: 2,
  },
  addon: {
    background: palette.common.white,
    borderRadius: '2px',
    boxShadow: 'rgba(0, 0, 0, 0.3) 0px 0px 2px, rgba(0, 0, 0, 0.3) 0px 4px 8px',
    boxSizing: 'initial',
    display: 'flex',
    justifyContent: 'space-between',
  },
  addonButton: {
    fontFamily: 'Montserrat',
    fontSize: '16px',
    paddingLeft: '2em',
    paddingRight: '2em',
    textTransform: 'none',
  },
  buttonCancel: {
    color: colors.unselected,
  },
  buttonApply: {
    color: colors.selected,
  },
}), { name: 'ColorPicker' });

const chromeStyles = {
  default: {
    picker: {
      fontFamily: 'Roboto',
      width: 'initial',
    },
  },
};

type Props = {
  name: string;
  value: string;
  disabled: boolean;
  onChange: (e: React.ChangeEvent) => void;
};

const ColorPicker = ({
  name,
  value,
  onChange,
  disabled,
}: Props) => {
  const classes = useStyles();
  const [isOpened, setOpened] = useState(false);
  const [color, setColor] = useState<string>('');

  const updateColor = useCallback((colorResult: ColorState) => setColor(colorResult.hex), [setColor]);

  const toggleOpened = useCallback(() => {
    if (!disabled) {
      setOpened((openedState) => !openedState);
    }
  }, [disabled, setOpened]);
  const close = useCallback(() => setOpened(false), [setOpened]);

  const containerRef = useOuterClick<HTMLSpanElement>(close);

  useEffect(() => {
    setColor(value);
  }, [value]);

  const applyColor = useCallback((e: React.KeyboardEvent | React.MouseEvent) => {
    if (color) {
      onChange({
        ...e,
        target: {
          // @ts-ignore
          name,
          value: color,
        },
      });
    }

    close();
  }, [onChange, color, close, name]);

  const keyPressHandler = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      applyColor(e);
    }
  }, [applyColor]);

  return (
    <span ref={containerRef}>
      <Button
        className={classes.button}
        type="button"
        variant="outlined"
        disabled={disabled}
        onClick={toggleOpened}
      >
        <Paper
          style={{
            background: value,
          }}
          square
          elevation={0}
          className={classes.colorBox}
        />
        {
          isOpened
            ? <SortUpIcon className={classes.buttonIcon} />
            : <SortDownIcon className={classes.buttonIcon} />
        }
      </Button>
      {!disabled && isOpened && (
        <Grid
          container
          className={classes.wrapper}
          onKeyPress={keyPressHandler}
        >
          <Grid item xs={12}>
            <ChromePicker
              color={color}
              styles={chromeStyles}
              onChangeComplete={updateColor}
              disableAlpha
            />
          </Grid>
          <Grid item xs={12} className={classes.addon}>
            <Button
              className={clsx(classes.addonButton, classes.buttonCancel)}
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              className={clsx(classes.addonButton, classes.buttonApply)}
              onClick={applyColor}
            >
              Apply
            </Button>
          </Grid>
        </Grid>
      )}
    </span>
  );
};

export default ColorPicker;
